import { parseNovelCorrectionResult, parseNovelMaterials, parseNovelRunDeleteResponse, parseNovelRunList, parseNovelRunStatusChange, parseNovelRunView, parseNovelTurnEvent, parseNovelTurnPage } from '@drama/contracts/novel'

/**
 * 互动小说对局与回合接口（/api/novel/runs/*）。回合走 NDJSON 流，事件见 contracts 的 NovelTurnEvent。
 * @param {{ request: Function, stream: Function }} transport
 */
export function createNovelRunsApi(transport, { prefix = '/api/novel/runs' } = {}) {
  const runPath = (run_id) => `${prefix}/${encodeURIComponent(run_id)}`
  return {
    /** @param {{ pack_id: string }} body 开局只收已上架作品，作品当前内容冻进对局快照 */
    createRun(body) {
      return transport.request('POST', prefix, { body, parse: parseNovelRunView })
    },
    /** @param {{ status?: 'active'|'archived' }} [filter] */
    listRuns({ status } = {}) {
      const query = status ? `?status=${encodeURIComponent(status)}` : ''
      return transport.request('GET', `${prefix}${query}`, { parse: parseNovelRunList })
    },
    /**
     * 对局视图。`history: 'none'` 取不带历史正文的同一视图（history 为空、history_omitted 为 true），轮询用（C1）。
     * @param {string} run_id
     * @param {{ history?: 'none' }} [options]
     */
    get(run_id, { history } = {}) {
      const query = history === 'none' ? '?history=none' : ''
      return transport.request('GET', `${runPath(run_id)}${query}`, { parse: parseNovelRunView })
    },
    /** @param {{ run_id: string, before_seq?: number, limit?: number }} args */
    listTurns({ run_id, before_seq, limit }) {
      const params = new URLSearchParams()
      if (before_seq !== undefined) params.set('before_seq', String(before_seq))
      if (limit !== undefined) params.set('limit', String(limit))
      const query = params.toString()
      return transport.request('GET', `${runPath(run_id)}/turns${query ? `?${query}` : ''}`, { parse: parseNovelTurnPage })
    },
    /** 玩家资料：在场人物、当前地点、物品、已知事实、关系、回忆册与其来源。 @param {string} run_id */
    getMaterials(run_id) {
      return transport.request('GET', `${runPath(run_id)}/materials?scope=player`, { parse: parseNovelMaterials })
    },
    /**
     * 回忆册纠错：以所选原文为依据更正一条记录，按 novel_correction 扣积分；返回刷新后的玩家资料与 charged_points。
     * 余额不足时 402 INSUFFICIENT_BALANCE（details 带 required_points、current_balance、shortfall_points）。
     * @param {{ run_id: string, record_id: string, text: string, evidence_ids: string[] }} args
     */
    correctMemory({ run_id, record_id, text, evidence_ids }) {
      return transport.request('POST', `${runPath(run_id)}/correction`, { body: { record_id, text, evidence_ids }, parse: parseNovelCorrectionResult })
    },
    /** 重试活动分支上失败的后台任务，返回对局视图。 @param {string} run_id */
    retryJobs(run_id) {
      return transport.request('POST', `${runPath(run_id)}/jobs/retry`, { body: {}, parse: parseNovelRunView })
    },
    /** @param {{ run_id: string, request_id: string, branch_id: string, parent_seq: number, mode: 'mixed'|'speech'|'action'|'continue', text: string }} args */
    streamTurn({ run_id, request_id, branch_id, parent_seq, mode, text }) {
      return transport.stream('POST', `${runPath(run_id)}/turn/stream`, {
        body: { request_id, branch_id, parent_seq, mode, text },
        parse: parseNovelTurnEvent,
      })
    },
    /** 取消生成中的回合（任一 API 进程上都生效，C12），返回对局视图。 @param {{ run_id: string, request_id: string }} args */
    cancelTurn({ run_id, request_id }) {
      return transport.request('POST', `${runPath(run_id)}/turn/cancel`, { body: { request_id }, parse: parseNovelRunView })
    },
    /** @param {{ run_id: string, request_id: string }} args */
    regenerate({ run_id, request_id }) {
      return transport.request('POST', `${runPath(run_id)}/regenerate`, { body: { request_id }, parse: parseNovelRunView })
    },
    /** @param {{ run_id: string, action: 'adopt'|'discard', branch_id: string }} args */
    decideCandidate({ run_id, action, branch_id }) {
      return transport.request('POST', `${runPath(run_id)}/candidate`, { body: { action, branch_id }, parse: parseNovelRunView })
    },
    /** @param {{ run_id: string, branch_id: string, kind: string, value: unknown, scope: string }} args */
    setPreference({ run_id, branch_id, kind, value, scope }) {
      return transport.request('POST', `${runPath(run_id)}/preferences`, { body: { branch_id, kind, value, scope }, parse: parseNovelRunView })
    },
    /** @param {string} run_id */
    archive(run_id) {
      return transport.request('POST', `${runPath(run_id)}/archive`, { body: {}, parse: parseNovelRunStatusChange })
    },
    /** 已归档的对局恢复为 active；不是 archived 时 409 CONFLICT。 @param {string} run_id */
    unarchive(run_id) {
      return transport.request('POST', `${runPath(run_id)}/unarchive`, { body: {}, parse: parseNovelRunStatusChange })
    },
    /** @param {string} run_id */
    remove(run_id) {
      return transport.request('DELETE', runPath(run_id), { parse: parseNovelRunDeleteResponse })
    },
  }
}
